/**
 * WELCHES TO-DO IST OFFEN — und welches schlägt das Plus vor
 * (Plan „Vorgangsdetail mobil", PR 1, 01.11.2026).
 *
 * Bis hierher trug jede `TodoKarteV4` ihr `offen` selbst. Die Seite wusste
 * also nicht, welches To-do gerade aufgeklappt ist — genau das braucht das
 * Plus auf dem Handy: „<Aktion> zu welchem To-do?" schlägt das gerade offene
 * vor. Deshalb liegt der Zustand seither auf der Seite (kontrolliert), und
 * hier stehen die reinen Regeln dazu:
 *
 *  · `umschalteOffen` — am Desktop dürfen mehrere To-dos offen sein, auf dem
 *    Handy (`akkordeon`) immer nur eines (Entscheidung 8 des Users:
 *    „Akkordeon vorerst nur mobil").
 *  · „zuletzt geöffnet" je Vorgang im `sessionStorage` — überlebt das
 *    Zuklappen und einen Abstecher in eine Mail, nicht aber den Tab.
 *  · `plusZielVorschlag` — die Vorauswahl im Ziel-Schritt des Plus.
 *
 * Alles node-prüfbar (kein DOM außer dem abgesicherten Speicherzugriff).
 */

/** Umschalten — `akkordeon` = höchstens ein To-do offen. */
export function umschalteOffen(
    offen: ReadonlySet<number>,
    todoId: number,
    auf: boolean,
    o: { akkordeon?: boolean } = {},
): ReadonlySet<number> {
    if (auf) {
        if (o.akkordeon) return offen.size === 1 && offen.has(todoId) ? offen : new Set([todoId]);
        if (offen.has(todoId)) return offen;
        const next = new Set(offen);
        next.add(todoId);
        return next;
    }
    if (!offen.has(todoId)) return offen;
    const next = new Set(offen);
    next.delete(todoId);
    return next;
}

/**
 * Beim Wechsel aufs Handy-Layout (schmales Fenster) dürfen nicht mehrere
 * offen bleiben: das zuletzt geöffnete gewinnt, sonst das erste offene.
 */
export function aufAkkordeon(offen: ReadonlySet<number>, zuletzt: number | null): ReadonlySet<number> {
    if (offen.size <= 1) return offen;
    if (zuletzt != null && offen.has(zuletzt)) return new Set([zuletzt]);
    return new Set([offen.values().next().value as number]);
}

const SCHLUESSEL = "kmvs.vg4.zuletztTodo.";

/** Zuletzt geöffnetes To-do dieses Vorgangs; null = keins oder kein Speicher. */
export function ladeZuletztGeoeffnet(vorgangId: number): number | null {
    try {
        const roh = window.sessionStorage.getItem(SCHLUESSEL + vorgangId);
        const n = roh == null ? NaN : Number(roh);
        return Number.isInteger(n) && n > 0 ? n : null;
    } catch {
        return null;
    }
}

export function merkeZuletztGeoeffnet(vorgangId: number, todoId: number): void {
    try {
        window.sessionStorage.setItem(SCHLUESSEL + vorgangId, String(todoId));
    } catch {
        // Privates Fenster, gesperrter Speicher: dann eben ohne Gedächtnis.
    }
}

/** Was der Vorschlag über ein To-do wissen muss. */
export type ZielTodo = { id: number; erledigt: boolean };

export type PlusZielLage = {
    /** Die To-dos des Vorgangs in Anzeigereihenfolge. */
    todos: readonly ZielTodo[];
    /** Gerade aufgeklappt (Handy: höchstens eines). */
    offen: ReadonlySet<number>;
    /** Zuletzt geöffnet in diesem Vorgang (`sessionStorage`). */
    zuletzt: number | null;
    /** Sprungmarke `?todo=`, solange sie aktiv ist. */
    sprung?: number | null;
    /**
     * Es gibt neben den To-dos noch ein weiteres Ziel (die laufende Notiz des
     * Vorgangs bei der Aktion „Notiz"). Dann kommt nie nur EIN Ziel in Frage.
     */
    weiteresZiel?: boolean;
};

export type PlusZielVorschlag = {
    /** Vorausgewähltes To-do; null = keine Vorauswahl, „Anlegen" bleibt gesperrt. */
    todoId: number | null;
    /**
     * Nur ein To-do kommt in Frage (Entscheidung 1 des Users, 01.11.2026):
     * Rückfrage überspringen und direkt dort anlegen.
     */
    ueberspringen: boolean;
};

/**
 * Die Vorauswahl im Ziel-Schritt (Plan §3b):
 *
 *   1. ein To-do ist aufgeklappt               → dieses
 *   2. zuletzt geöffnet, und es gibt es noch   → dieses (auch erledigt —
 *      Entscheidung 2: erledigte dürfen Ziel sein)
 *   3. genau ein offenes To-do                 → dieses
 *   4. Sprungmarke                             → das angesprungene
 *   5. sonst                                   → keine; nichts landet still
 *                                                am falschen To-do
 *
 * ÜBERSPRINGEN: Die Kandidaten sind die OFFENEN To-dos; hat der Vorgang nur
 * erledigte, sind es diese. Bleibt genau einer übrig (und kein weiteres Ziel
 * wie die laufende Notiz), fragt das Plus nicht. Ein erledigtes To-do erreicht
 * man dann über „+ Hinzufügen" am aufgeklappten To-do selbst.
 */
export function plusZielVorschlag(l: PlusZielLage): PlusZielVorschlag {
    const ids = new Set(l.todos.map((t) => t.id));
    const offeneTodos = l.todos.filter((t) => !t.erledigt);
    const kandidaten = offeneTodos.length > 0 ? offeneTodos : l.todos;
    if (kandidaten.length === 1 && !l.weiteresZiel) {
        return { todoId: kandidaten[0]!.id, ueberspringen: true };
    }
    const aufgeklappt = l.todos.find((t) => l.offen.has(t.id));
    if (aufgeklappt) return { todoId: aufgeklappt.id, ueberspringen: false };
    if (l.zuletzt != null && ids.has(l.zuletzt)) return { todoId: l.zuletzt, ueberspringen: false };
    if (offeneTodos.length === 1) return { todoId: offeneTodos[0]!.id, ueberspringen: false };
    if (l.sprung != null && ids.has(l.sprung)) return { todoId: l.sprung, ueberspringen: false };
    return { todoId: null, ueberspringen: false };
}

/**
 * Welches To-do auf dem Handy beim Öffnen des Vorgangs aufgeklappt ist
 * (Artifact „Vorgangsdetail mobil": „standardmäßig das erste"): das zuletzt
 * geöffnete, wenn es noch offen ist, sonst das erste offene, sonst keins.
 */
export function startOffen(todos: readonly ZielTodo[], zuletzt: number | null): number | null {
    const offene = todos.filter((t) => !t.erledigt);
    if (zuletzt != null && offene.some((t) => t.id === zuletzt)) return zuletzt;
    return offene[0]?.id ?? null;
}
